// Parity P1 wave 1: the attention strip's host (hybrid design §3.1 "Attention strip (not a
// stack): one compact line per pending approval, open question or unseen run result, expandable
// to the full card; the title never moves"). P1 hosts it; the items and their cards are other
// packages' (P2 registers the `attention` detail extension and the card per kind through
// `registerTaskCard`). An item without a registered card is left out; no items, no strip.
import { Button, cn } from "@companyos/ui";
import { ChevronDownIcon, ChevronRightIcon, CircleAlertIcon } from "lucide-react";
import { useState } from "react";
import { type TaskCan, type TaskCardItem, taskCardFor } from "../task-layout/registry";
import type { TaskDetail } from "../view-engine";

/** The task's attention items (`extensions.attention`), each with a registered card. */
export function attentionItems(task: TaskDetail): TaskCardItem[] {
  const raw = (task as { extensions?: Record<string, unknown> }).extensions?.attention;
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (x): x is TaskCardItem =>
      typeof x === "object" &&
      x !== null &&
      typeof (x as TaskCardItem).kind === "string" &&
      typeof (x as TaskCardItem).id === "string" &&
      taskCardFor(x as TaskCardItem) !== undefined,
  );
}

export function AttentionStrip({ task, can }: { task: TaskDetail; can: TaskCan }) {
  const items = attentionItems(task);
  const [open, setOpen] = useState<string | null>(null);
  if (!items.length) return null;
  return (
    <section
      aria-label={`Attention (${items.length})`}
      data-slot="attention-strip"
      className="border-b border-subtle bg-warning-subtle/40 px-6 py-1.5 max-md:px-4"
    >
      <ul className="m-0 flex list-none flex-col p-0">
        {items.map((item) => {
          const card = taskCardFor(item);
          const key = `${item.kind}:${item.id}`;
          const expanded = open === key;
          const summary = typeof item.summary === "string" ? item.summary : item.kind;
          return (
            <li key={key} className="flex flex-col">
              <div className="flex min-h-7 items-center gap-2 text-sm">
                <CircleAlertIcon
                  aria-hidden="true"
                  className="size-3.5 shrink-0 text-warning-text"
                />
                <span className={cn("min-w-0 flex-1", !expanded && "truncate")}>{summary}</span>
                <Button
                  variant="ghost"
                  size="xs"
                  aria-expanded={expanded}
                  onClick={() => setOpen(expanded ? null : key)}
                >
                  {expanded ? (
                    <ChevronDownIcon aria-hidden="true" />
                  ) : (
                    <ChevronRightIcon aria-hidden="true" />
                  )}
                  {expanded ? "Less" : "Open"}
                </Button>
              </div>
              {expanded && card ? (
                <div className="pb-2">
                  <card.Component item={item} can={can} />
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
